import { useEffect, useState } from 'react';
import { apiService, getUploadsBaseUrl } from '../services/apiService';

/**
 * Company logo for sidebar/header after login.
 * - Full http(s) URLs (e.g. Supabase public URL) are used directly.
 * - Otherwise loads via GET /auth/me/company-logo (blob) so local disk paths work without exposing raw paths to the browser.
 */
export function useCompanyLogo(currentUserId: string | undefined | null): string | null {
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const refresh = () => setRefreshTick((n) => n + 1);
    window.addEventListener('company-logo-updated', refresh);
    return () => window.removeEventListener('company-logo-updated', refresh);
  }, []);

  useEffect(() => {
    let revokeBlob: (() => void) | undefined;
    let cancelled = false;

    const run = async () => {
      const token = localStorage.getItem('authToken');
      // Logo is tied to the JWT company; do not require currentUser.id (legacy sessions may omit it).
      if (!token) {
        setLogoSrc(null);
        return;
      }
      let path: string | null = null;
      try {
        const me = (await apiService.get('/auth/me')) as {
          company?: { logo_file_path?: string | null } | null;
        };
        const raw = me?.company?.logo_file_path;
        path = raw && String(raw).trim() ? String(raw).trim() : null;
      } catch {
        path = null;
      }

      try {
        const blob = await apiService.download('/auth/me/company-logo');
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revokeBlob = () => URL.revokeObjectURL(url);
        setLogoSrc(url);
        return;
      } catch {
        // fall through to URL/path-based fallback
      }

      try {
        if (!path) {
          if (!cancelled) setLogoSrc(null);
          return;
        }
        if (/^https?:\/\//i.test(path)) {
          const bust = path.includes('?') ? '&' : '?';
          if (!cancelled) setLogoSrc(`${path}${bust}t=${Date.now()}`);
          return;
        }
        const baseUrl = getUploadsBaseUrl().replace(/\/+$/, '');
        const normalized = path.replace(/^\.\//, '').replace(/\\/g, '/');
        const tail = normalized.toLowerCase().includes('uploads/')
          ? normalized.split(/uploads\//i).pop() || ''
          : normalized.replace(/^\/+/, '');
        const rel = tail.startsWith('uploads/') ? `/${tail}` : `/uploads/${tail.replace(/^\/+/, '')}`;
        if (!cancelled) setLogoSrc(`${baseUrl}${rel}`);
      } catch {
        if (!cancelled) setLogoSrc(null);
      }
    };

    run();
    return () => {
      cancelled = true;
      revokeBlob?.();
    };
  }, [currentUserId, refreshTick]);

  return logoSrc;
}

/** Company logo as a data URL so it survives receipt print (iframe copy). */
export async function fetchCompanyLogoDataUrl(): Promise<string | null> {
  try {
    const blob = await apiService.download('/auth/me/company-logo');
    if (!blob || blob.size < 32) return null;
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
